import { css } from 'styled-components';

// Base reset CSS template that all components will use
const BASE_COMPONENT_RESET = css`
  word-break: auto-phrase;
  hyphens: auto;
  padding: 0;
  margin: 0;

  /* Text settings */
  & {
    text-rendering: optimizeSpeed;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-family:
      'Bw Nista Grotesk',
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      Roboto,
      Oxygen,
      Ubuntu,
      Cantarell,
      'Open Sans',
      'Helvetica Neue',
      sans-serif;
  }

  /* Remove list styles on ul, ol elements */
  ul,
  ol {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  /* Make images easier to work with */
  img,
  picture {
    max-width: 100%;
    display: block;
  }

  /* Remove all animations and transitions for people that prefer not to see them */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`;

// Dropdown component reset CSS
export const DROPDOWN_RESET = css`
  /* Box sizing rules */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* Text settings */
  & {
    text-rendering: optimizeSpeed;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-family:
      'Bw Nista Grotesk',
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      Roboto,
      Oxygen,
      Ubuntu,
      Cantarell,
      'Open Sans',
      'Helvetica Neue',
      sans-serif;
  }

  /* Make images easier to work with */
  img,
  picture {
    max-width: 100%;
    display: block;
  }
`;

// Button component reset CSS
export const BUTTON_RESET = css`
  ${BASE_COMPONENT_RESET}

  /* Button-specific resets */
  button {
    border: none;
    background: none;
    cursor: pointer;
    padding: 0;
  }
`;

// Icon component reset CSS
export const ICON_RESET = css`
  ${BASE_COMPONENT_RESET}

  /* Icon-specific resets */
  svg {
    display: inline-block;
    vertical-align: middle;
  }
`;

// IconButton component reset CSS
export const ICON_BUTTON_RESET = css`
  ${BASE_COMPONENT_RESET}

  /* IconButton-specific resets */
  button {
    border: none;
    background: none;
    cursor: pointer;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

// InfoTile component reset CSS
export const INFO_TILE_RESET = css`
  ${BASE_COMPONENT_RESET}
  p, span {
    margin: 0;
    padding: 0;
  }

  /* InfoTile-specific resets */
  div {
    display: flex;
  }
`;

// Input component reset CSS
export const INPUT_RESET = css`
  ${BASE_COMPONENT_RESET}

  /* Input-specific resets */
  input {
    border: none;
    outline: none;
    background: transparent;
  }

  label {
    display: block;
  }

  p,
  span {
    margin: 0;
    padding: 0;
  }
`;

// List component reset CSS
export const LIST_RESET = css`
  ${BASE_COMPONENT_RESET}

  /* List-specific resets */
  li {
    list-style-position: inside;
  }
`;

// Loader component reset CSS
export const LOADER_RESET = css`
  ${BASE_COMPONENT_RESET}

  /* Loader-specific resets */
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// Modal component reset CSS
export const MODAL_RESET = css`
  ${BASE_COMPONENT_RESET}

  /* Modal-specific resets */
  position: relative;

  /* Prevent content from overflowing */
  overflow: hidden;
`;

// Selector component reset CSS
export const SELECTOR_RESET = css`
  ${BASE_COMPONENT_RESET}
  p, span {
    margin: 0;
    padding: 0;
  }

  /* Selector-specific resets */
  label {
    cursor: pointer;
  }

  input[type='radio'],
  input[type='checkbox'] {
    margin: 0;
    padding: 0;
  }
`;

// Summary component reset CSS
export const SUMMARY_RESET = css`
  ${BASE_COMPONENT_RESET}

  /* Summary-specific resets */
  table {
    border-collapse: collapse;
    width: 100%;
  }

  th,
  td {
    text-align: left;
  }
`;

// Tabs component reset CSS
export const TABS_RESET = css`
  ${BASE_COMPONENT_RESET}
`;

// TermsCheckbox component reset CSS
export const TERMS_CHECKBOX_RESET = css`
  ${BASE_COMPONENT_RESET}

  /* TermsCheckbox-specific resets */
  input[type="checkbox"] {
    margin: 0;
    padding: 0;
  }

  label {
    cursor: pointer;
  }
`;

// Textarea component reset CSS
export const TEXTAREA_RESET = css`
  ${BASE_COMPONENT_RESET}

  p, span {
    margin: 0;
    padding: 0;
  }

  /* Textarea-specific resets */
  textarea {
    border: none;
    outline: none;
    resize: vertical;
    background: transparent;
  }

  label {
    display: block;
  }
`;

// Tooltip component reset CSS
export const TOOLTIP_RESET = css`
  ${BASE_COMPONENT_RESET}

  /* Tooltip-specific resets */
  position: relative;
`;

// Text component reset CSS
export const TYPOGRAPHY_RESET = css`
  ${BASE_COMPONENT_RESET}

  margin: 0;
  padding: 0;
`;

// PulseHighlight component reset CSS
export const PULSE_HIGHLIGHT_RESET = css`
  ${BASE_COMPONENT_RESET}

  /* PulseHighlight-specific resets */
  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
`;
